import { Box, Card, SxProps } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import {
  RaRecord,
  RecordContextProvider,
  ResourceContextProvider,
  useTranslate,
} from 'react-admin';

import { DeleteButton, DeleteButtonPropsI } from '@sh4res/bo-common/components/DeleteButton';
import { Resources } from '@sh4res/bo-common/utils';

interface ShowSectionProps {
  label: string;
  children: ReactNode;
  record?: RaRecord;
  resource?: Resources;
  noCard?: boolean;
  sx?: SxProps;
  labelParams?: Record<string, unknown>;
  leftAligned?: boolean;
  subtitle?: string;
  withDelete?: DeleteButtonPropsI;
  additionalButtons?: ReactElement;
}

export const subtitleStyle = {
  fontSize: '11px',
  color: 'DarkGray',
  fontWeight: '400',
  fontStyle: 'italic',
  marginTop: '3px',
};

const containerStyle = {
  margin: '2px 2px 15px 2px',
  padding: 0,
  border: 'none',
  // `<fieldset>` has a browser-default `min-width: min-content`
  minWidth: 0,
};

export const ShowSection = ({
  label,
  labelParams,
  children,
  record,
  resource,
  noCard,
  sx,
  leftAligned,
  subtitle,
  withDelete,
  additionalButtons,
}: ShowSectionProps) => {
  const translate = useTranslate();
  const translatedLabel = translate(label, labelParams);
  const translatedSubtitle = subtitle ? translate(subtitle) : null;

  const header = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: `13px 15px 13px ${leftAligned ? 0 : 15}px`,
        fontWeight: '600',
      }}>
      <Box component="legend">
        {translatedLabel}
        {translatedSubtitle && <Box sx={subtitleStyle}>{translatedSubtitle}</Box>}
      </Box>
      <Box>
        {!!additionalButtons && additionalButtons}
        {withDelete && <DeleteButton {...withDelete} testId={withDelete.testId} />}
      </Box>
    </Box>
  );

  let childrenContentWithContext =
    record !== undefined ? (
      <RecordContextProvider value={record}>{children}</RecordContextProvider>
    ) : (
      children
    );

  childrenContentWithContext =
    resource !== undefined ? (
      <ResourceContextProvider value={resource}>
        {childrenContentWithContext as ReactElement}
      </ResourceContextProvider>
    ) : (
      childrenContentWithContext
    );

  if (noCard) {
    return (
      <Box sx={{ ...containerStyle, ...sx }} component="fieldset">
        {header}
        {childrenContentWithContext}
      </Box>
    );
  } else {
    return (
      <Card sx={{ ...containerStyle, ...sx }} component="fieldset">
        {header}
        {childrenContentWithContext}
      </Card>
    );
  }
};
